/* Tiny shared LMS shell + API client. */
import { Link } from "react-router-dom";
import "./portal.css";

const LOGO = "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png";

export function LmsShell({ subtitle = "Student Portal", children, rightSlot, wide = false, bgImage = null }) {
  const shellStyle = bgImage
    ? {
        backgroundImage: `linear-gradient(rgba(5,8,7,0.72), rgba(5,8,7,0.88)), url('${bgImage}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : undefined;
  return (
    <div className="tbg-lms">
      <div className="tbg-lms-bar">
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <img src={LOGO} alt="TBG" />
        </Link>
        <span className="sub">{subtitle}</span>
        <span className="spacer" />
        {rightSlot}
      </div>
      <div className={`tbg-lms-shell${wide ? " wide" : ""}`} style={shellStyle}>{children}</div>
    </div>
  );
}

/* ------------------------- API client ------------------------- */
const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "tbg_student_token";
const EXP_KEY = "tbg_student_token_exp";

export const getStudentToken = () => {
  const exp = localStorage.getItem(EXP_KEY);
  if (exp && new Date(exp) < new Date()) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXP_KEY);
    return null;
  }
  return localStorage.getItem(TOKEN_KEY);
};

export const setStudentSession = (data) => {
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(EXP_KEY, data.expires_at);
};

export const clearStudentSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXP_KEY);
};

export const studentApi = {
  setPassword: (token, password) =>
    postJson(`${API}/students/set-password`, { token, password }),
  login: (email, password) =>
    postJson(`${API}/students/login`, { email, password }),
  requestMagicLink: (email) =>
    postJson(`${API}/students/request-magic-link`, { email }),
  schoolRegister: ({ school_id, first_name, last_name, password }) =>
    postJson(`${API}/students/school-register`, { school_id, first_name, last_name, password }),
  schoolLogin: ({ school_id, first_name, last_name, password }) =>
    postJson(`${API}/students/school-login`, { school_id, first_name, last_name, password }),
  me: () => authedGet(`${API}/students/me`),
  dashboardSummary: () => authedGet(`${API}/students/dashboard-summary`),
  setGoal: (target_date) => authedPost(`${API}/students/goal`, { target_date }),  downloadProgressReport: async () => {
    const t = getStudentToken();
    const r = await fetch(`${API}/students/me/progress-report.pdf`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!r.ok) {
      const detail = await r.text().catch(() => "");
      const err = new Error(detail || "Download failed");
      err.status = r.status;
      throw err;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TGI-ProgressReport.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  },
};

export const ttsApi = {
  // Public — list of voice options
  voices: () => fetch(`${API}/tts/voices`).then((r) => r.json()),
  // Generate (or fetch cached) natural-voice audio
  generate: ({ text, voice }) => authedPost(`${API}/tts/generate`, { text, voice }),
  // Convert the server-returned relative audio URL into an absolute one for <audio>
  resolveAudioUrl: (relUrl) => `${API.replace(/\/api$/, "")}${relUrl}`,
};


export const paymentsApi = {
  checkout: (packageId, opts = {}) =>
    postJson(`${API}/payments/checkout`, {
      package_id: packageId,
      origin_url: window.location.origin,
      ...(opts.customer_email ? { customer_email: opts.customer_email } : {}),
      ...(opts.customer_name ? { customer_name: opts.customer_name } : {}),
    }),
  status: (sessionId) => fetch(`${API}/payments/status/${sessionId}`).then(mustOk).then((r) => r.json()),
  receipts: () => authedGet(`${API}/payments/receipts`),
  downloadReceipt: async (sessionId, invoiceNumber) => {
    // Student-auth download; falls back to session-id-only anon URL if no
    // token is available (e.g., on /enroll/success before login). The
    // backend accepts both patterns — see receipts.download_receipt_pdf.
    const t = getStudentToken();
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    const r = await fetch(`${API}/payments/receipts/${sessionId}`, { headers });
    if (!r.ok) {
      const detail = await r.text().catch(() => "");
      const err = new Error(detail || "Download failed");
      err.status = r.status;
      throw err;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${invoiceNumber || "invoice"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  },
};

export const certsApi = {
  verify: (number) => postJson(`${API}/certificates/verify`, { number }),
  mine: () => authedGet(`${API}/certificates/mine`),
  /* Download the PDF as a blob (Authorization header required — a plain <a href>
     link would fail auth). Triggers a browser download named after the cert. */
  download: async (number) => {
    const t = getStudentToken();
    const r = await fetch(`${API}/certificates/${encodeURIComponent(number)}/pdf`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!r.ok) {
      const detail = await r.text().catch(() => "");
      const err = new Error(detail || "Download failed");
      err.status = r.status;
      throw err;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TGI-Certificate-${number}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  },
};

export const lmsApi = {
  progress: () => authedGet(`${API}/lms/progress`),
  completeLesson: (lessonId) => authedPost(`${API}/lms/lessons/${lessonId}/complete`, {}),
  startQuiz: (moduleId) => authedPost(`${API}/lms/modules/${moduleId}/quiz/start`, {}),
  submitQuiz: (moduleId, attemptId, answers) =>
    authedPost(`${API}/lms/modules/${moduleId}/quiz/submit`, { attempt_id: attemptId, answers }),
  startExam: () => authedPost(`${API}/lms/exam/start`, {}),
  submitExam: (attemptId, answers) =>
    authedPost(`${API}/lms/exam/submit`, { attempt_id: attemptId, answers }),
  examHistory: () => authedGet(`${API}/lms/exam/history`),
  courseFull: (courseId) => authedGet(`${API}/courses/${courseId}`),
};

export const adminApi = {
  courseAuthoring: (courseId) => adminAuthedGet(`${API}/lms/admin/courses/${courseId}`),
  updateModule: (courseId, moduleId, body) =>
    adminAuthedRequest("PUT", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}`, body),
  createLesson: (courseId, moduleId, body) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/lessons`, body),
  updateLesson: (courseId, moduleId, lessonId, body) =>
    adminAuthedRequest("PUT", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`, body),
  deleteLesson: (courseId, moduleId, lessonId) =>
    adminAuthedRequest("DELETE", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`),
  listQuestions: (courseId, moduleId) =>
    adminAuthedGet(`${API}/lms/admin/courses/${courseId}/modules/${moduleId}/questions`),
  createQuestion: (courseId, moduleId, body) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/questions`, body),
  updateQuestion: (questionId, body) =>
    adminAuthedRequest("PUT", `${API}/lms/admin/questions/${questionId}`, body),
  deleteQuestion: (questionId) =>
    adminAuthedRequest("DELETE", `${API}/lms/admin/questions/${questionId}`),
  draftQuestion: (courseId, moduleId, body) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/questions/draft`, body),
  batchDraftFromLesson: (courseId, moduleId, lessonId) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}/questions/batch-draft`),
  coverageGapAnalysis: (courseId, moduleId, includePending = false) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/coverage-gap-analysis?include_pending=${includePending ? "true" : "false"}`),
  reassignQuestion: (questionId, patch) =>
    adminAuthedRequest("PATCH", `${API}/lms/admin/questions/${questionId}/reassign`, patch),
  toggleMark: (questionId, marked = true) =>
    adminAuthedRequest("PATCH", `${API}/lms/admin/questions/${questionId}/mark`, { marked }),
  batchDraftForLo: (courseId, moduleId, targetLo) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/questions/batch-draft-for-lo`, { target_lo: targetLo }),
  bulkImport: (courseId, items) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/questions/bulk-import`, { items }),
  approveAuthorPending: (courseId, moduleId) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/modules/${moduleId}/questions/approve-author-pending`),
  courseCoverageGap: (courseId, includePending = true) =>
    adminAuthedRequest("POST", `${API}/lms/admin/courses/${courseId}/coverage-gap-analysis?include_pending=${includePending ? "true" : "false"}`),
  courseMissingDiagrams: (courseId) =>
    adminAuthedGet(`${API}/lms/admin/courses/${courseId}/missing-diagrams`),
  listLessonAssets: (lessonId) =>
    adminAuthedGet(`${API}/lms/admin/lessons/${lessonId}/assets`),
  uploadLessonAsset: (lessonId, body) =>
    adminAuthedRequest("POST", `${API}/lms/admin/lessons/${lessonId}/assets`, body),
  deleteLessonAsset: (lessonId, slug) =>
    adminAuthedRequest("DELETE", `${API}/lms/admin/lessons/${lessonId}/assets/${slug}`),
  lessonAssetUrl: (lessonId, slug) =>
    `${API}/lms/lessons/${lessonId}/assets/${slug}`,
  // --- TTS warmup (pre-generate lesson narration) ---
  ttsWarmupStart: (courseId, voices) =>
    adminAuthedRequest("POST", `${API}/tts/warmup/start`,
                       { course_id: courseId, voices: voices || null }),
  ttsWarmupStatus: (jobId) =>
    adminAuthedGet(`${API}/tts/warmup/status/${jobId}`),
  ttsWarmupCoverage: (courseId) =>
    adminAuthedGet(`${API}/tts/warmup/coverage/${courseId}`),
};

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return mustJson(res);
}

async function authedGet(url) {
  const t = getStudentToken();
  const res = await fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return mustJson(res);
}

async function authedPost(url, body) {
  const t = getStudentToken();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  return mustJson(res);
}

/* ------------------------- Admin API helpers ------------------------- */
const ADMIN_TOKEN_KEY = "tbg_admin_token";
const ADMIN_EXP_KEY = "tbg_admin_token_exp";

export const getAdminToken = () => {
  const exp = localStorage.getItem(ADMIN_EXP_KEY);
  if (exp && new Date(exp) < new Date()) {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXP_KEY);
    return null;
  }
  return localStorage.getItem(ADMIN_TOKEN_KEY);
};

async function adminAuthedGet(url) {
  const t = getAdminToken();
  const res = await fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return mustJson(res);
}

async function adminAuthedRequest(method, url, body) {
  const t = getAdminToken();
  const res = await fetch(url, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return mustJson(res);
}

async function mustOk(res) {
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `HTTP ${res.status}`);
  }
  return res;
}

async function mustJson(res) {
  const t = await res.text();
  let data = {};
  try { data = t ? JSON.parse(t) : {}; } catch (e) { /* noop */ }
  if (!res.ok) {
    const err = new Error(data.detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}
